import type { ActionReturnType } from '../../types';
import { ManagementProgress } from '../../../types';

import {
  CUSTOM_BG_CACHE_NAME,
  DEBUG,
  LANG_CACHE_NAME,
  LOCK_SCREEN_ANIMATION_DURATION_MS,
  MEDIA_CACHE_NAME,
  MEDIA_CACHE_NAME_AVATARS,
  MEDIA_PROGRESSIVE_CACHE_NAME,
} from '../../../config';
import { updateAppBadge } from '../../../util/appBadge';
import { PASSCODE_IDB_STORE } from '../../../util/browser/idb';
import {
  IS_WEBM_SUPPORTED, MAX_BUFFER_SIZE, PLATFORM_ENV,
} from '../../../util/browser/windowEnvironment';
import * as cacheApi from '../../../util/cacheApi';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { ACCOUNT_SLOT, getAccountsInfo } from '../../../util/multiaccount';
import { unsubscribe } from '../../../util/notifications';
import { clearEncryptedSession, encryptSession, forgetPasscode } from '../../../util/passcode';
import { parseInitialLocationHash, resetInitialLocationHash, resetLocationHash } from '../../../util/routing';
import { pause } from '../../../util/schedulers';
import {
  clearStoredSession,
  loadStoredSession,
  storeSession,
} from '../../../util/sessions';
import { forceWebsync } from '../../../util/websync';
import {
  callApi, callApiLocal, initApi, setShouldEnableDebugLog,
} from '../../../api/gramjs';
import { removeGlobalFromCache, removeSharedStateFromCache, serializeGlobal } from '../../cache';
import {
  addActionHandler, getGlobal, setGlobal,
} from '../../index';
import {
  clearGlobalForLockScreen, updateManagementProgress, updatePasscodeSettings,
} from '../../reducers';
import { selectSharedSettings } from '../../selectors/sharedState';
import { destroySharedStatePort } from '../../shared/sharedStateConnector';

addActionHandler('initApi', (global, actions): ActionReturnType => {
  const initialLocationHash = parseInitialLocationHash();
  const {
    shouldAllowHttpTransport,
    shouldForceHttpTransport,
    shouldDebugExportedSenders,
    shouldCollectDebugLogs,
    language,
  } = selectSharedSettings(global);

  const hasTestParam = window.location.search.includes('test') || initialLocationHash?.tgWebAuthTest === '1';

  const accountsInfo = getAccountsInfo();
  const accountIds = Object.values(accountsInfo).map(({ userId }) => userId)?.filter(Boolean);

  void initApi(actions.apiUpdate, {
    userAgent: navigator.userAgent,
    platform: PLATFORM_ENV,
    sessionData: loadStoredSession(),
    isWebmSupported: IS_WEBM_SUPPORTED,
    maxBufferSize: MAX_BUFFER_SIZE,
    webAuthToken: initialLocationHash?.tgWebAuthToken,
    dcId: initialLocationHash?.tgWebAuthDcId ? Number(initialLocationHash?.tgWebAuthDcId) : undefined,
    mockScenario: initialLocationHash?.mockScenario,
    shouldAllowHttpTransport: false,
    shouldForceHttpTransport: false,
    shouldDebugExportedSenders,
    langCode: language,
    isTestServerRequested: hasTestParam,
    accountIds,
  });

  void setShouldEnableDebugLog(Boolean(shouldCollectDebugLogs));
});

// 在未登录场景下，给 authIsLoading 增加一个最大等待时间，避免登录页长时间转圈
const AUTH_LOADING_TIMEOUT_MS = 15000;

function scheduleAuthLoadingTimeout() {
  setTimeout(() => {
    let global = getGlobal();
    // 只在仍未登录且仍处于 loading 时才兜底关闭
    if (!global.authIsLoading) return;
    if (global.authState === 'authorizationStateReady') return;

    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
  }, AUTH_LOADING_TIMEOUT_MS);
}

addActionHandler('setAuthPhoneNumber', (global, actions, payload): ActionReturnType => {
  const { phoneNumber } = payload!;

  void callApi('provideAuthPhoneNumber', phoneNumber.replace(/[^\d]/g, ''));

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('setAuthCode', (global, actions, payload): ActionReturnType => {
  const { code } = payload!;

  void callApi('provideAuthCode', code);

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('setAuthPassword', (global, actions, payload): ActionReturnType => {
  const { password } = payload!;

  void callApi('provideAuthPassword', password);

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('uploadProfilePhoto', async (global, actions, payload): Promise<void> => {
  const {
    file, isFallback, isVideo, videoTs, bot,
    tabId = getCurrentTabId(),
  } = payload!;

  global = updateManagementProgress(global, ManagementProgress.InProgress, tabId);
  setGlobal(global);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.info('[SSG] uploadProfilePhoto start', {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      isFallback,
      isVideo,
      videoTs,
      hasBot: Boolean(bot),
    });
  }

  const result = await callApi('uploadProfilePhoto', file, isFallback, isVideo, videoTs, bot);
  if (!result) return;

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.info('[SSG] uploadProfilePhoto success', result);
  }

  global = getGlobal();
  global = updateManagementProgress(global, ManagementProgress.Complete, tabId);
  setGlobal(global);

  actions.loadFullUser({ userId: global.currentUserId! });
});

addActionHandler('signUp', (global, actions, payload): ActionReturnType => {
  const { firstName, lastName } = payload!;

  void callApi('provideAuthRegistration', { firstName, lastName });

  const nextGlobal = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(nextGlobal);
  scheduleAuthLoadingTimeout();
  return nextGlobal;
});

addActionHandler('returnToAuthPhoneNumber', (global): ActionReturnType => {
  void callApi('restartAuth');

  return {
    ...global,
    authErrorKey: undefined,
  };
});

function getSsgDeviceId(): string {
  const key = 'ssg_device_id';
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    deviceId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

function mapSsgAuthError(err: any): string {
  const message = typeof err?.message === 'string' ? err.message : '';
  const normalized = message.toUpperCase();

  if (normalized.includes('PHONE_NUMBER_OCCUPIED') || normalized.includes('ACCOUNT_OCCUPIED')) {
    return '账号已注册';
  }

  if (normalized.includes('IP_BLACK')) {
    return '当前网络不可用，请更换网络后重试';
  }

  if (normalized.includes('IP_UPPER_LIMIT') || normalized.includes('DEVICE_UPPER_LIMIT')) {
    return '注册次数已达上限，请稍后再试';
  }

  if (
    normalized.includes('PHONE_NUMBER_INVALID')
    || normalized.includes('ACCOUNT_INVALID')
    || normalized.includes('PASSWORD_INVALID')
    || normalized.includes('PASSWORD_NOT_RIGHT')
    || normalized.includes('PASSWORD_HASH_INVALID')
    || normalized.includes('AUTH_KEY_UNREGISTERED')
  ) {
    return '账号或者密码错误';
  }

  if (normalized.includes('FLOOD_WAIT') || normalized.includes('TOO_MANY_REQUESTS')) {
    return '操作频繁，请稍后再试';
  }

  if (normalized.includes('NETWORK') || normalized.includes('TIMEOUT')) {
    return '网络异常，请稍后再试';
  }

  return '系统繁忙，请稍后再试';
}

addActionHandler('ssgSignIn', async (global, actions, payload): Promise<void> => {
  const { account, password } = payload!;

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    await callApi('ssgSignIn', account, password, getSsgDeviceId());

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
    };
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
  }
});

addActionHandler('ssgGoToRegister', async (global, actions, payload): Promise<void> => {
  const { account, password } = payload!;

  global = getGlobal();
  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    await callApi('ssgCheckAccountAndPassword', account, password);

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authState: 'authorizationStateWaitRegistration',
      ssgRegAccount: account,
      ssgRegPassword: password,
    };
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
  }
});

addActionHandler('ssgSignUp', async (global, actions, payload): Promise<void> => {
  const { firstName, avatarFile } = payload!;

  global = getGlobal();
  const account = global.ssgRegAccount;
  const password = global.ssgRegPassword;

  if (!account || !password) {
    return;
  }

  global = {
    ...global,
    authIsLoading: true,
    authErrorKey: undefined,
  };
  setGlobal(global);
  scheduleAuthLoadingTimeout();

  try {
    await callApi('ssgSignUp', account, password, firstName, getSsgDeviceId());

    // 标记「刚注册完成」，进入聊天列表主页面时会自动刷新一次以拉全接待群等
    try {
      window.sessionStorage.setItem('ssgram_just_registered', '1');
    } catch {
      // ignore
    }

    if (avatarFile) {
      actions.uploadProfilePhoto({ file: avatarFile, tabId: getCurrentTabId() });
    }

    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      ssgRegAccount: undefined,
      ssgRegPassword: undefined,
    };
    setGlobal(global);
  } catch (err: any) {
    global = getGlobal();
    global = {
      ...global,
      authIsLoading: false,
      authErrorKey: mapSsgAuthError(err),
    };
    setGlobal(global);
  }
});

addActionHandler('goToAuthQrCode', (global): ActionReturnType => {
  void callApi('restartAuthWithQr');

  return {
    ...global,
    authIsLoadingQrCode: true,
    authErrorKey: undefined,
  };
});

addActionHandler('saveSession', (global, actions, payload): ActionReturnType => {
  if (global.passcode.isScreenLocked) {
    return;
  }

  const { sessionData } = payload;
  if (sessionData) {
    storeSession(sessionData);
  } else {
    clearStoredSession();
  }
});

addActionHandler('signOut', async (global, actions, payload): Promise<void> => {
  if ('hangUp' in actions) actions.hangUp({ tabId: getCurrentTabId() });
  if ('leaveGroupCall' in actions) actions.leaveGroupCall({ tabId: getCurrentTabId() });

  try {
    window.sessionStorage.setItem('ssgram_just_logged_out', '1');
  } catch {
    // ignore
  }

  try {
    resetInitialLocationHash();
    resetLocationHash();
    await unsubscribe();
    await Promise.race([callApi('destroy'), pause(3000)]);
    await forceWebsync(false);
  } catch (err) {
    // Do nothing
  }

  actions.reset();

  if (payload?.forceInitApi) {
    actions.initApi();
  }
});

addActionHandler('requestChannelDifference', (global, actions, payload): ActionReturnType => {
  const { chatId } = payload;

  void callApi('requestChannelDifference', chatId);
});

addActionHandler('reset', (global, actions): ActionReturnType => {
  clearStoredSession(ACCOUNT_SLOT);
  clearEncryptedSession();

  void cacheApi.clear(MEDIA_CACHE_NAME);
  void cacheApi.clear(MEDIA_CACHE_NAME_AVATARS);
  void cacheApi.clear(MEDIA_PROGRESSIVE_CACHE_NAME);
  void cacheApi.clear(CUSTOM_BG_CACHE_NAME);

  removeGlobalFromCache();
  destroySharedStatePort();

  // Check if there are any accounts left
  const accounts = getAccountsInfo();
  if (!Object.values(accounts).length) {
    PASSCODE_IDB_STORE.clear();
    removeSharedStateFromCache();
  }

  const langCachePrefix = LANG_CACHE_NAME.replace(/\d+$/, '');
  const langCacheVersion = Number((LANG_CACHE_NAME.match(/\d+$/) || ['0'])[0]);
  for (let i = 0; i < langCacheVersion; i++) {
    void cacheApi.clear(`${langCachePrefix}${i === 0 ? '' : i}`);
  }

  updateAppBadge(0);

  actions.initShared({ force: true });
  Object.values(global.byTabId).forEach(({ id: otherTabId, isMasterTab }) => {
    actions.init({ tabId: otherTabId, isMasterTab });
  });
});

addActionHandler('disconnect', (): ActionReturnType => {
  void callApiLocal('disconnect');
});

addActionHandler('destroyConnection', (): ActionReturnType => {
  void callApiLocal('destroy', true, true);
});

addActionHandler('loadNearestCountry', async (global): Promise<void> => {
  if (global.connectionState !== 'connectionStateReady') {
    return;
  }

  const authNearestCountry = await callApi('fetchNearestCountry');

  global = getGlobal();
  global = {
    ...global,
    authNearestCountry,
  };
  setGlobal(global);
});

addActionHandler('setDeviceToken', (global, actions, deviceToken): ActionReturnType => {
  return {
    ...global,
    push: {
      deviceToken,
      subscribedAt: Date.now(),
    },
  };
});

addActionHandler('deleteDeviceToken', (global): ActionReturnType => {
  return {
    ...global,
    push: undefined,
  };
});

addActionHandler('lockScreen', async (global): Promise<void> => {
  const sessionJson = JSON.stringify({ ...loadStoredSession(), userId: global.currentUserId });
  const globalJson = await serializeGlobal(global);

  await encryptSession(sessionJson, globalJson);
  forgetPasscode();
  clearStoredSession();
  updateAppBadge(0);

  global = getGlobal();
  global = updatePasscodeSettings(
    global,
    {
      isScreenLocked: true,
      invalidAttemptsCount: 0,
      timeoutUntil: undefined,
    },
  );
  setGlobal(global);

  setTimeout(() => {
    global = getGlobal();
    global = clearGlobalForLockScreen(global);
    setGlobal(global);
  }, LOCK_SCREEN_ANIMATION_DURATION_MS);

  try {
    await unsubscribe();
    await callApi('destroy', true);
  } catch (err) {
    // Do nothing
  }
});
